import React from "react"
import Link from "next/link"
import {Card, CardBody, CardSubtitle, CardText, CardTitle,} from "reactstrap"
import Image from "../CustomImage"
// import Icon from "../Icon"
// import Swiper from "react-id-swiper"
import dynamic from "next/dynamic";
import {FilterSelection} from "../../types/filterSelection";
import {useRouter} from "next/router";
import {getImageUrl} from "../../services/image";

interface CottageCardProps {
    cottageId: number,
    code: string
    priceForStay: any
    priceInfo: string
    startDate: string
    endDate: string
    slug: string,
    name: string,
    description: string,
    images: string[],
    lowestWeekPrice: number,
    highestWeekPrice: number,
    searchSelection: FilterSelection,
    tags: any,
    searchInformation: any
    isAutomaticallyTranslated: boolean
    county?: string
    country?: string
}

let Swiper = dynamic(
    () => import("react-id-swiper"),
    {ssr: false}
);

let CardOverlayHearth = dynamic(
    () => import("../Favourites/CardOverlayHearth"),
    {ssr: false}
);

const getDogsText = (amountOfDogs) => {
    return amountOfDogs > 1 ? 'honden' : 'hond';
}

const CottageCard = (
    {
        cottageId,
        code,
        priceForStay,
        priceInfo,
        startDate,
        endDate,
        slug,
        name,
        description,
        images,
        lowestWeekPrice,
        highestWeekPrice,
        searchSelection,
        searchInformation,
        tags,
        isAutomaticallyTranslated,
        county,
        country
    }: CottageCardProps) => {
    const params = {
        pagination: {
            el: '.swiper-pagination',
            type: 'fraction',
        },
        navigation: {
            nextEl: '.swiper-button-next',
            prevEl: '.swiper-button-prev'
        }
    }

    const hasDogsSelection = searchSelection.dogs && searchSelection.dogs > 0;
    const priceForStayWithDogs = hasDogsSelection ? priceForStay + (searchSelection.dogs * 25) : null;

    const router = useRouter();
    const {urlParams} = searchSelection.getPropertiesForLink();

    if (router.query.destination) {
        urlParams.destination = router.query.destination.toString();
    }

    urlParams['aankomst-datum'] = startDate;
    urlParams['eind-datum'] = endDate;

    return (
        <Card className="h-100 border-0 shadow">
            <div className="card-img-top overflow-hidden swiper-container-cottage-card">
                <Swiper {...params}>
                    {images.map((image, index) => (
                        <div key={image}>
                            <Link
                                prefetch={false}
                                href={{
                                    pathname: '/vakantiehuizen/[slug]',
                                    query: urlParams,
                                }}
                                as={{
                                    pathname: `/vakantiehuizen/${slug}`,
                                    query: urlParams,
                                }}
                                passHref
                            >
                                <a aria-label="Ga naar cottage details">
                                    <div style={{width: '100%', height: '100%', position: 'relative'}}>
                                    <Image
                                        placeholder="blur"
                                        blurDataURL={getImageUrl('image-loader2.jpeg')}
                                        src={`${image}`}
                                        alt={`Vakantiehuis in ${country} ${name}`}
                                        layout="fill"
                                        objectFit="cover"
                                        className="img-fluid rounded"
                                        sizes="(max-width:576px) 100vw, (max-width:991px) 50vw, (max-width:1199px) 30vw, 20vw"
                                    />
                                    </div>
                                </a>
                            </Link>
                        </div>
                    ))}
                </Swiper>
                <Link
                    prefetch={false}
                    href={{
                        pathname: '/vakantiehuizen/[slug]',
                        query: urlParams,
                    }}
                    as={{
                        pathname: `/vakantiehuizen/${slug}`,
                        query: urlParams,
                    }}
                    passHref
                >
                    <a className="tile-link" aria-label="Ga naar cottage details"/>
                </Link>
                <CardOverlayHearth
                    cottageId={cottageId}
                />
            </div>
            <CardBody className="d-flex align-items-center">
                <div className="w-100">
                    <span className="text-primary small">{country}, {county}</span>
                    <CardTitle className="mb-1 mt-1">
                        <Link
                            prefetch={false}
                            href={{
                                pathname: '/vakantiehuizen/[slug]',
                                query: urlParams,
                            }}
                            as={{
                                pathname: `/vakantiehuizen/${slug}`,
                                query: urlParams,
                            }}
                            passHref
                        >
                            <a
                                aria-label="Ga naar cottage details"
                                className="text-decoration-none text-dark h6">
                                {name}
                            </a>
                        </Link>
                    </CardTitle>

                    <div className="mb-2">
                        <span className="small">Huiscode: {code}</span>
                    </div>

                    {tags && (
                        <ul className="list-inline text-sm mb-4">
                            {tags.map((tag) => (
                                <li key={tag.value} className="list-inline-item mr-3">
                                    <i
                                        className={`fa fa-${tag.icon} mr-1 text-secondary`}
                                    />{" "}
                                    {tag.value}
                                </li>
                            ))}
                        </ul>
                    )}

                    {isAutomaticallyTranslated &&
                        <p className="font-italic small font-weight-bold">
                            Automatisch vertaald
                        </p>
                    }
                    {description &&
                        <CardSubtitle className="d-flex mb-3">
                            <div className="flex-grow-1 mb-0 text-sm"
                                 dangerouslySetInnerHTML={{__html: description}}
                            />
                            {/*<p className="flex-shrink-1 mb-0 card-stars text-xs text-right">*/}
                            {/*</p>*/}
                        </CardSubtitle>
                    }
                    <CardText>
                        {priceForStay &&
                            <>
                        <span className="h4 text-secondary">
                            &euro;{priceForStay}-
                            {priceForStayWithDogs &&
                                <span className="ml-2 text-sm">
                                    <span className="text-black text-xsm">( incl. {searchSelection.dogs} {getDogsText(searchSelection.dogs)}</span> &euro; {priceForStayWithDogs},-<span className="text-black text-xsm"> )</span>
                                </span>
                            }
                          </span>
                                <br/>
                                <span className="text-xsm font-weight-bold">{priceInfo}</span>
                            </>
                        }
                        {!priceForStay && lowestWeekPrice &&
                            <>
                        <span className="h4 text-secondary">
                            &euro;{lowestWeekPrice}-&euro;{highestWeekPrice}
                          </span>
                                &nbsp;per week
                            </>
                        }
                        {!priceForStay && !lowestWeekPrice &&
                            <>
                                <b>Prijs op aanvraag</b>
                            </>
                        }
                    </CardText>
                </div>
            </CardBody>
        </Card>
    )
}

export default CottageCard;

